const getProductFormErrorMessage = (response, fallback) => {
  const findMessage = (value) => {
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.map(findMessage).find(Boolean) || '';
    if (value && typeof value === 'object') {
      for (const key of ['description', 'message', 'errors']) {
        const message = findMessage(value[key]);
        if (message) return message;
      }
    }
    return '';
  };

  return findMessage(response) || fallback;
};

const parseProductFormResponse = (response) =>
  response.text().then((text) => {
    if (!text) return { body: {}, ok: response.ok };

    try {
      return { body: JSON.parse(text), ok: response.ok };
    } catch {
      return { body: { description: text.trim() }, ok: response.ok };
    }
  });

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;
        let addSucceeded = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const loadingSpinner = this.querySelector('.loading__spinner');
        loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const variantId = String(formData.get('id') ?? '').trim();
        if (!/^\d+$/.test(variantId) || variantId === '0') {
          const variantErrorMessage =
            window.variantStrings?.unavailable || "Please choose an available option.";
          this.error = true;
          this.handleErrorMessage(variantErrorMessage);
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          loadingSpinner?.classList.add('hidden');
          return;
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then(parseProductFormResponse)
          .then(async ({ body: response, ok }) => {
            if (!ok || response?.status) {
              const errorMessage = getProductFormErrorMessage(
                response,
                "We couldn't add this item. Please try again."
              );
              this.error = true;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response?.errors || errorMessage,
                message: response?.message || errorMessage,
              });
              this.handleErrorMessage(errorMessage);
              this.dispatchCartErrorEvent(errorMessage, 'INVALID');
              linesUpdateDeferred?.reject(new Error(errorMessage));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              soldOutMessage?.classList.add('hidden');
              this.submitButtonText.classList.remove('hidden');
              this.submitButton.removeAttribute('aria-disabled');
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            if (!response?.id && !response?.items?.some((item) => item?.id)) {
              throw new Error('Add-to-cart response is missing the added item.');
            }
            addSucceeded = true;
            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.renderCartContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              await this.renderCartContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            if (addSucceeded) {
              window.location.assign(window.routes.cart_url);
              return;
            }
            const networkErrorMessage = "We couldn't confirm the update. Please check your cart before trying again.";
            this.handleErrorMessage(networkErrorMessage);
            this.dispatchCartErrorEvent(networkErrorMessage, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            loadingSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async renderCartContents(response) {
        try {
          if (this.cart.renderContents(response) !== false) return;
          if (await this.cart.refreshContents?.()) return;
        } catch (error) {
          console.error(error);
        }
        window.location.assign(window.routes.cart_url);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

